import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, IconButton, Tooltip, AppBar, Toolbar,
  useMediaQuery, useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import TranslateIcon from '@mui/icons-material/Translate';
import MenuIcon from '@mui/icons-material/Menu';
import { clearJwtToken, isLoggedIn } from '../utils/auth';
import { useI18n } from '../utils/i18n';

const DRAWER_WIDTH = 220;

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang, toggleLang } = useI18n();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const loggedIn = isLoggedIn();

  const navItems = [
    { label: lang === 'zh' ? '日内交易' : 'Day Trader', path: '/dashboard', icon: <ShowChartIcon /> },
    { label: t('nav.stockAnalysis'), path: '/stock', icon: <TrendingUpIcon /> },
    { label: t('nav.recommendations'), path: '/recommendations', icon: <AutoAwesomeIcon /> },
  ];

  const handleLogout = () => { clearJwtToken(); navigate('/'); window.location.reload(); };
  const isSelected = (path) => path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname.startsWith(path);
  const handleNav = (path) => { navigate(path); if (isMobile) setMobileOpen(false); };

  const drawerContent = (
    <>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" color="primary" fontWeight={700}>Sorane</Typography>
        <Tooltip title={lang === 'en' ? '切换中文' : 'Switch to English'}>
          <IconButton size="small" onClick={toggleLang} sx={{ color: 'text.secondary' }}>
            <TranslateIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      <List sx={{ px: 1, mt: 1, flex: 1 }}>
        {navItems.map((item) => (
          <ListItemButton key={item.path} selected={isSelected(item.path)} onClick={() => handleNav(item.path)}
            sx={{ borderRadius: 2, mb: 0.5, '&.Mui-selected': { bgcolor: 'rgba(108,99,255,0.12)' } }}>
            <ListItemIcon sx={{ minWidth: 36, color: isSelected(item.path) ? 'primary.main' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      <Box sx={{ p: 1 }}>
        {loggedIn ? (
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={t('nav.logout')} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItemButton>
        ) : (
          <ListItemButton onClick={() => handleNav('/login')} sx={{ borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}><LoginIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={t('nav.login') || 'Login'}
              secondary={t('nav.loginHint') || 'Higher rate limits'}
              primaryTypographyProps={{ fontSize: 14 }}
              secondaryTypographyProps={{ fontSize: 11, color: 'text.disabled' }}
            />
          </ListItemButton>
        )}
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {isMobile && (
        <AppBar position="fixed" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1, color: 'text.primary' }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" color="primary" fontWeight={700}>Sorane</Typography>
          </Toolbar>
        </AppBar>
      )}

      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.paper' } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, bgcolor: 'background.paper', borderRight: '1px solid rgba(255,255,255,0.06)' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box component="main" sx={{
        flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto',
        ...(isMobile ? { mt: '56px' } : {}),
      }}>
        <Outlet />
      </Box>
    </Box>
  );
}
